import { createContext, useContext, useEffect, useState } from 'react';

const LanguageContext = createContext();

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) throw new Error('useLanguage must be used within LanguageProvider');
  return context;
};

const translations = {
  en: {
    nav: {
      about: 'About',
      projects: 'Projects',
      contact: 'Contact',
      back: 'Back',
    },
    hero: {
      greeting: 'Hello',
      name: 'Your Idea. My Code. Done.',
      name1: 'Your Idea.',
      name2: 'My Code.',
      name3: 'Done.',
      title: 'Developer & Freelancer',
      subtitle: 'I create websites, bots, and automation solutions that make your life easier.',
      available: 'Available for projects',
    },
    about: {
      title: 'About Me',
      text: `Hey, I'm Lino - a passionate programmer. I develop Python applications and websites that solve real problems. From automated web scraping tools and Discord bots to modern frontend websites - I find creative solutions for your challenges.`
    },
    projects: {
      title: 'Projects',
      subtitle: 'Here are some of the things I have built',
      viewSource: 'Source',
      viewDemo: 'Demo',
    },
    skills: {
      title: 'Skills & Technologies',
    },
    cta: {
      line1: "Let's build",
      line2: 'something great.',
    },
    contact: {
      title: 'Get in Touch',
      subtitle: "Have a project in mind? Let's talk about it!",
      name: 'Your Name',
      email: 'Your Email',
      message: 'Your Message',
      send: 'Send Message',
    },
    footer: {
      rights: 'All rights reserved.',
      privacy: 'Privacy Policy',
    }
  },
  de: {
    nav: {
      about: 'Über mich',
      projects: 'Projekte',
      contact: 'Kontakt',
      back: 'Zurück',
    },
    hero: {
      greeting: 'Hey',
      name: 'Deine Idee. Mein Code. Fertig.',
      name1: 'Deine Idee.',
      name2: 'Mein Code.',
      name3: 'Fertig.',
      title: 'Programmierer & Freelancer',
      subtitle: 'Ich erstelle Websites, Bots und Automatisierungslösungen, die dein Leben einfacher machen.',
      available: 'Verfügbar für Projekte',
    },
    about: {
      title: 'Über mich',
      text: 'Hey, ich bin Lino - begeisterter Programmierer. Ich entwickle Python-Anwendungen und Websites, die echte Probleme lösen. Von automatisierten Web-Scraping-Tools über Discord-Bots bis hin zu modernen Frontend-Websites - ich finde kreative Lösungen für deine Herausforderungen.',
    },
    projects: {
      title: 'Projekte',
      subtitle: 'Hier sind einige Dinge, die ich gebaut habe',
      viewSource: 'Quellcode',
      viewDemo: 'Demo',
    },
    skills: {
      title: 'Fähigkeiten & Technologien',
    },
    cta: {
      line1: 'Lass uns etwas',
      line2: 'Großartiges bauen.',
    },
    contact: {
      title: 'Kontakt aufnehmen',
      subtitle: 'Hast du ein Projekt im Kopf? Lass uns darüber reden!',
      name: 'Dein Name',
      email: 'Deine Email',
      message: 'Deine Nachricht',
      send: 'Nachricht senden',
    },
    footer: {
      rights: 'Alle Rechte vorbehalten.',
      privacy: 'Datenschutz',
    }
  }
};

export const LanguageProvider = ({ children }) => {
  const [language, setLanguage] = useState(() => {
    const saved = localStorage.getItem('language');
    if (saved) return saved;
    const browserLang = navigator.language?.split('-')[0];
    return browserLang === 'de' ? 'de' : 'en';
  });

  useEffect(() => {
    localStorage.setItem('language', language);
    document.documentElement.lang = language;
  }, [language]);

  const toggleLanguage = () => {
    setLanguage(prev => prev === 'en' ? 'de' : 'en');
  };

  const t = (key) => {
    const keys = key.split('.');
    let value = translations[language];
    for (const k of keys) {
      value = value?.[k];
    }
    return value || key;
  };

  return (
    <LanguageContext.Provider value={{ language, toggleLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
};
